import { Controller } from '@hotwired/stimulus'

type SortingMode = 'asc' | 'desc'
interface SortInfo {
  index: number
  content: string
}

export default class extends Controller<HTMLTableCellElement> {
  static targets = ['icon']
  static values = {
    index: Number,
    defaultSort: String
  }

  declare readonly iconTarget: HTMLElement
  declare readonly indexValue: number
  declare readonly hasDefaultSortValue: boolean
  declare readonly defaultSortValue: string

  connect(): void {
    if (this.hasDefaultSortValue) {
      const initialSort = this.defaultSortValue as SortingMode
      this.element.dataset.sorting = initialSort
      this.element.dataset.sortingByThis = 'true'

      this.#performSort(initialSort)
    }
  }

  #resetCurrentSorting(): void {
    for (const header of this.element.parentElement?.querySelectorAll('th') ?? []) {
      if (header.dataset.sortingByThis !== undefined) {
        delete header.dataset.sortingByThis
        break
      }
    }
  }

  #newSortingMode(previousSorting: string | undefined): SortingMode {
    const sort = this.element.dataset.sorting
    const wasAlreadySortingByThis = previousSorting !== undefined

    if (wasAlreadySortingByThis) {
      return sort === 'asc' ? 'desc' : 'asc'
    }
    return 'asc'
  }

  #sorter(sortingMode: SortingMode): (a: SortInfo, b: SortInfo) => -1 | 0 | 1 {
    return (a, b) => {
      const aNum = parseFloat(a.content)
      const bNum = parseFloat(b.content)

      const aVal = !isNaN(aNum) && !isNaN(bNum) ? aNum : a.content
      const bVal = !isNaN(aNum) && !isNaN(bNum) ? bNum : b.content

      if (aVal < bVal) return sortingMode === 'asc' ? -1 : 1
      if (aVal > bVal) return sortingMode === 'asc' ? 1 : -1
      return 0
    }
  }

  // NOTE: this event will only be attached for tables in boxes
  sortByThis(e: InputEvent): void {
    e.preventDefault()
    const previousSorting = this.element.dataset.sorting

    this.#resetCurrentSorting()
    const newSorting = this.#newSortingMode(previousSorting)
    this.element.dataset.sorting = newSorting
    this.element.dataset.sortingByThis = 'true'

    this.#performSort(newSorting)
  }

  #performSort(sortingMode: SortingMode): void {
    // identify body rows and organise content in those rows with index
    const table = this.element.parentElement?.closest('table')
    if (table === null || table === undefined) return
    const allTableRows = table?.querySelectorAll('tr') ?? []
    const bodyRows = Array.from(allTableRows).filter((_, index) => index > 0)

    const contentPlusIndexPerRow = bodyRows.map((row, index) => {
      const cells = row.querySelectorAll('td')
      const content = cells[this.indexValue]?.innerText.trim() ?? ''
      return { content, index }
    })

    // sort rows according to the content of the cell
    const sortedRows = contentPlusIndexPerRow
      .sort(this.#sorter(sortingMode))
      .map(({ index }) => bodyRows[index])

    // remove all rows from the table
    table.querySelector('tbody')?.remove()

    // append the sorted rows to the table
    const tbody = document.createElement('tbody')
    for (const row of sortedRows) {
      tbody.appendChild(row)
    }
    table.appendChild(tbody)
  }
}
